import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import adminApiClient, {
  ContentProfileGiftInput,
  ContentProfileInput,
} from "../../../api/admin";

export const CONTENT_PROFILES_QUERY_KEY = ["content-profiles"];

export const useContentProfiles = () => {
  return useQuery({
    queryKey: CONTENT_PROFILES_QUERY_KEY,
    queryFn: () => adminApiClient.getContentProfiles(),
  });
};

export const useContentProfile = (id: string | null) => {
  return useQuery({
    queryKey: [...CONTENT_PROFILES_QUERY_KEY, id],
    queryFn: () => adminApiClient.getContentProfile(id as string),
    enabled: !!id,
  });
};

// Manual refresh, not polled — this goes through AdminService.getClocksByIds,
// a BigQuery query, which carries real per-call latency (job-scheduling
// overhead alone is typically 1-2s+) regardless of result size. Polling it
// every few seconds means re-running that query continuously for the whole
// time this tab is open. FeatureBetaGroups.tsx's own BigQuery-backed list
// doesn't auto-poll either — same reasoning, matched here rather than
// reintroduced.
export const useContentProfileClocks = (
  id: string | null,
  params?: { skip?: number; limit?: number; search?: string },
) => {
  return useQuery({
    queryKey: [...CONTENT_PROFILES_QUERY_KEY, id, "clocks", params],
    queryFn: () => adminApiClient.getContentProfileClocks(id as string, params),
    enabled: !!id,
    placeholderData: (previous) => previous,
  });
};

export const useClockContentProfiles = (clockId: string) => {
  return useQuery({
    queryKey: [...CONTENT_PROFILES_QUERY_KEY, "by-clock", clockId],
    queryFn: () => adminApiClient.getClockContentProfiles(clockId),
    enabled: !!clockId,
  });
};

export const useCreateContentProfile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ContentProfileInput) =>
      adminApiClient.createContentProfile(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CONTENT_PROFILES_QUERY_KEY });
    },
  });
};

export const useUpdateContentProfile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<ContentProfileInput>;
    }) => adminApiClient.updateContentProfile(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CONTENT_PROFILES_QUERY_KEY });
    },
  });
};

export const useDeleteContentProfile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => adminApiClient.deleteContentProfile(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CONTENT_PROFILES_QUERY_KEY });
    },
  });
};

export const useAddClocksToContentProfile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, clockIds }: { id: string; clockIds: string[] }) =>
      adminApiClient.addClocksToContentProfile(id, clockIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CONTENT_PROFILES_QUERY_KEY });
    },
  });
};

export const useRemoveClocksFromContentProfile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, clockIds }: { id: string; clockIds: string[] }) =>
      adminApiClient.removeClocksFromContentProfile(id, clockIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CONTENT_PROFILES_QUERY_KEY });
    },
  });
};

export const useMarkContentProfileReady = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => adminApiClient.markContentProfileReady(id),
    onSuccess: () => {
      // Marking an order profile ready can submit it right away.
      queryClient.invalidateQueries({ queryKey: CONTENT_PROFILES_QUERY_KEY });
    },
  });
};

export const useSyncContentProfile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      pushDespiteClockMismatch,
    }: {
      id: string;
      pushDespiteClockMismatch?: boolean;
    }) => adminApiClient.syncContentProfile(id, pushDespiteClockMismatch),
    onSuccess: () => {
      // A sync changes pushedAt, which changes every assigned device's
      // derived status — refresh the clocks list, not just the profile itself.
      queryClient.invalidateQueries({ queryKey: CONTENT_PROFILES_QUERY_KEY });
    },
  });
};

// A profile's gift lives in its own Mongo document (GiftService), entirely
// separate from the profile's own fields — not invalidated by anything else
// in this file.
const giftQueryKey = (id: string) => [
  ...CONTENT_PROFILES_QUERY_KEY,
  id,
  "gift",
];

export const useContentProfileGift = (id: string | null) => {
  return useQuery({
    queryKey: giftQueryKey(id ?? ""),
    queryFn: () => adminApiClient.getContentProfileGift(id as string),
    enabled: !!id,
  });
};

export const useUpsertContentProfileGift = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ContentProfileGiftInput) =>
      adminApiClient.upsertContentProfileGift(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: giftQueryKey(id) });
    },
  });
};

export const useDeleteContentProfileGift = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => adminApiClient.deleteContentProfileGift(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: giftQueryKey(id) });
    },
  });
};
